import { EChart } from "@/lib/EChart";
import type { Infringement, InfringementSourceAntiPiracy } from "@/types/domain";
import { useThemeColors } from "@/hooks/useThemeColors";
import { useFilteredInfringements } from "@/hooks/useFilteredInfringements";
import { useDashboardFilter } from "@/contexts/DashboardFilterContext";
import { WidgetShell } from "@/components/dashboard/WidgetShell";

interface TopPirateServicesChartProps {
  infringements: Infringement[];
  onServiceClick?: (service: string) => void;
  activeService?: string;
}

function TopPirateServicesChart({
  infringements,
  onServiceClick,
  activeService,
}: TopPirateServicesChartProps) {
  const colors = useThemeColors();

  const serviceCounts = new Map<string, number>();
  infringements.forEach((inf) => {
    const service = (inf.details as InfringementSourceAntiPiracy).pirate_service || "Unknown";
    serviceCounts.set(service, (serviceCounts.get(service) || 0) + 1);
  });

  const entries = Array.from(serviceCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7);

  const services = entries.map(([service]) => service);
  const counts = entries.map(([, count]) => count);

  const chartColors = [
    colors.chartRed,
    colors.chartOrange,
    colors.chartAmber,
    colors.chartCyan,
    colors.chartBlue,
    colors.chartViolet,
    colors.chartPink,
  ];

  const option = {
    backgroundColor: "transparent",
    grid: { left: "25%", right: "8%", top: "5%", bottom: "5%", containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: colors.surfaceElevated,
      borderColor: colors.border,
      borderWidth: 1,
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      formatter: (params: any) => {
        const item = params[0];
        return `<div style="font-weight:600;margin-bottom:4px;">${item.name}</div>
                <div style="display:flex;align-items:center;gap:8px;">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${item.color}"></span>
                  <span style="font-weight:600;font-size:14px;">${item.value} infringement${item.value !== 1 ? "s" : ""}</span>
                </div>`;
      },
    },
    xAxis: {
      type: "value",
      splitLine: { lineStyle: { color: colors.border, width: 1 } },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: colors.textMuted, fontSize: 10, fontFamily: "Inter, sans-serif" },
    },
    yAxis: {
      type: "category",
      data: services,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: colors.textSecondary,
        fontSize: 11,
        fontWeight: 500,
        fontFamily: "Inter, sans-serif",
      },
    },
    series: [
      {
        type: "bar",
        data: counts.map((value, index) => ({
          value,
          itemStyle: {
            color: chartColors[index % chartColors.length],
            borderRadius: [0, 4, 4, 0],
            opacity: activeService && activeService !== services[index] ? 0.3 : 1,
          },
        })),
        barMaxWidth: 20,
        emphasis: { itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.3)" } },
      },
    ],
  };

  return (
    <div className="h-full">
      {infringements.length === 0 ? (
        <div className="flex items-center justify-center h-full text-sm text-muted">
          No data available
        </div>
      ) : (
        <EChart
          option={option}
          style={{ height: "100%" }}
          onEvents={
            onServiceClick
              ? {
                  click: (params: any) => onServiceClick(params.name),
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

export function TopPirateServicesWidget() {
  const { infringements } = useFilteredInfringements();
  const { filters, setFilter } = useDashboardFilter();

  return (
    <WidgetShell title="Top Pirate Services">
      <div className="flex-1 min-h-0 px-4 pb-2">
        <TopPirateServicesChart
          infringements={infringements}
          onServiceClick={(service) => {
            if (service !== "Unknown") {
              setFilter({ pirateService: service });
            }
          }}
          activeService={filters.pirateService}
        />
      </div>
    </WidgetShell>
  );
}
